import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  reanalysisGaveUp,
  type Asset,
  type AssetKind,
  type AssetManifest,
  type AssetThumbnail,
} from "@scenix/core";
import { ApiError, api, type AnalysisStage, type UnfillableStage } from "../lib/api";
import { engineLabel, engineName, listOf, stageProduces, stagesForKind } from "../lib/analysis";
import { sourceLabel } from "../lib/assetSource";
import { SourceLink } from "../components/SourceLink";
import { Crumbs } from "../components/Crumbs";
import { timecode } from "../lib/scrub";
import { Transcript, usePlayer } from "../components/Transcript";
import { AssetTimeline } from "../components/AssetTimeline";

/**
 * One asset, in full: the media, everything analysis found, and the breakdown
 * over time that lets you get the shape of a video without watching it
 * (ADR 000024).
 */
export function AssetRoute() {
  const { spaceId = "", assetId = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Owns the media element, so the transcript can follow and seek it.
  // Destructured rather than kept as one object: the compiler infers anything
  // reaching a ref prop is ref-like, and reading it back off the object then
  // looks like touching a ref during render.
  const { attach, element, at, seek, following, setFollowing } = usePlayer();

  // Annotating is editor work, the same as bringing content in, so a viewer
  // reads the timeline without the drag or the form.
  const space = useQuery({
    queryKey: ["space", spaceId],
    queryFn: () => api.getSpace(spaceId as never),
  });

  // The corpus, already cached by the content page, for finding neighbours.
  const siblings = useQuery({
    queryKey: ["assets", spaceId, ""],
    queryFn: () => api.listAssets(spaceId as never),
  });

  const detail = useQuery({
    queryKey: ["asset", spaceId, assetId],
    queryFn: () => api.getAsset(spaceId as never, assetId as never),
    refetchInterval: (query) => {
      const status = query.state.data?.asset.status;
      return status && status !== "ready" && status !== "failed" ? 4000 : false;
    },
  });

  const remove = useMutation({
    mutationFn: () => api.deleteAsset(spaceId as never, assetId as never),
    onSuccess: () => navigate(`/spaces/${spaceId}/content`),
  });

  // Which give-up the reader has closed, rather than whether they have closed
  // one. This route is not remounted when the asset in the path changes, so a
  // boolean would carry one asset's dismissal onto the next asset's note, and a
  // second pass that gave up for a new reason would arrive already hidden.
  const [dismissed, setDismissed] = useState("");

  // Where a piece of content came from, attachable after the fact: a corpus
  // uploaded without provenance is the ordinary case rather than the exception
  // (ADR 000060).
  const [editingSource, setEditingSource] = useState(false);
  const [sourceDraft, setSourceDraft] = useState("");
  const [sourceFailure, setSourceFailure] = useState<string | null>(null);

  const setSource = useMutation({
    mutationFn: (sourceUrl: string) =>
      api.updateAsset(spaceId as never, assetId as never, { sourceUrl }),
    onSuccess: async () => {
      setEditingSource(false);
      setSourceFailure(null);
      await queryClient.invalidateQueries({ queryKey: ["asset", spaceId, assetId] });
    },
    onError: (error: unknown) =>
      setSourceFailure(error instanceof ApiError ? error.userMessage : "Could not save that."),
  });

  // Puts a failed asset back in analyzing, so the worker's own claim loop
  // picks it up again. Without this, the only recovery from a transient
  // failure was deleting the asset and uploading the whole thing again
  // (issue #68).
  const retry = useMutation({
    mutationFn: () => api.retryAnalysis(spaceId as never, assetId as never),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["asset", spaceId, assetId] }),
  });

  // Editing what a person wrote is Editor-only on the API, so a viewer is not
  // offered a control that would only fail.
  const mayEdit = space.data?.role !== "viewer";

  if (detail.isPending) return <p role="status">Loading…</p>;

  if (detail.error) {
    const missing = detail.error instanceof ApiError && detail.error.isNotFound;
    return (
      <section className="empty">
        <h1>{missing ? "No such content" : "Could not load this"}</h1>
        <p className="muted">{missing ? "It is not in this space." : describe(detail.error)}</p>
        <p>
          <Link to={`/spaces/${spaceId}/content`}>Back to the content</Link>
        </p>
      </section>
    );
  }

  const {
    asset,
    manifest,
    moments,
    mediaUrl,
    incompleteStages,
    unfillableStages,
    manifestUnreadable,
  } = detail.data;
  const poster = asset.thumbnailPath
    ? api.assetThumbnailUrl(spaceId as never, asset.id, asset.thumbnailPath)
    : undefined;

  return (
    <section>
      <header className="page-head">
        <Crumbs spaceId={spaceId}>
          {" · "}
          <Link to={`/spaces/${spaceId}/content`}>Content</Link>
        </Crumbs>
        <h1>
          {asset.name}
          {/* Called out beyond the meta line, so a model's guess does not read
              as a primary source at a glance (ADR 000037). */}
          {asset.source === "generated" && <span className="source-badge">Generated</span>}
        </h1>
        <p className="muted">
          {asset.kind}
          {` · ${sourceLabel(asset.source)}`}
          {asset.importMode && ` · ${asset.importMode}`}
          {asset.durationSec > 0 && ` · ${timecode(asset.durationSec)}`}
          {asset.analysisEngine && ` · ${asset.analysisEngine}`}
        </p>
        {editingSource ? (
          <form
            className="row source-edit"
            onSubmit={(event) => {
              event.preventDefault();
              setSource.mutate(sourceDraft.trim());
            }}
          >
            <label>
              <span className="visually-hidden">Where this came from</span>
              <input
                value={sourceDraft}
                placeholder="https://www.youtube.com/watch?v=…"
                onChange={(event) => setSourceDraft(event.target.value)}
              />
            </label>
            <button type="submit" disabled={setSource.isPending}>
              Save
            </button>
            <button type="button" className="ghost" onClick={() => setEditingSource(false)}>
              Cancel
            </button>
          </form>
        ) : asset.sourceUrl ? (
          <p className="muted source-line">
            <SourceLink sourceUrl={asset.sourceUrl} />
            <a href={asset.sourceUrl} target="_blank" rel="noreferrer noopener">
              {asset.sourceUrl}
            </a>
            {mayEdit && (
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  setSourceDraft(asset.sourceUrl ?? "");
                  setEditingSource(true);
                }}
              >
                Edit
              </button>
            )}
          </p>
        ) : (
          mayEdit && (
            <p className="muted">
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  setSourceDraft("");
                  setEditingSource(true);
                }}
              >
                Say where this came from
              </button>
            </p>
          )
        )}
        {sourceFailure && (
          <p role="alert" className="error">
            {sourceFailure}
          </p>
        )}
      </header>

      {/* Ingest state is always visible: an asset stuck in analyzing is a
          support question, and one that silently never finished is a lost
          afternoon (ADR 000028). */}
      {asset.status !== "ready" && (
        <p className={asset.status === "failed" ? "error" : "muted"}>
          {asset.status === "failed"
            ? asset.failureReason || "Analysis failed."
            : `${asset.status}…`}
          {asset.status === "failed" && (
            <button
              type="button"
              className="ghost"
              disabled={retry.isPending}
              onClick={() => retry.mutate()}
            >
              {retry.isPending ? "Retrying…" : "Try again"}
            </button>
          )}
        </p>
      )}

      {/* The third ingest state, and the one that looks like no state at all:
          a re-analysis that gave up leaves the asset ready and the reason on
          it, so without this the page is byte for byte what it was before the
          request (ADR 000120). Drawn as a caution rather than an error because
          the asset is fine and it was the request that was not. */}
      {reanalysisGaveUp(asset) && dismissed !== gaveUp(asset) && (
        <p className="analysis-gave-up" role="status">
          The last re-analysis gave up and left this as it was: {asset.failureReason}
          <button type="button" className="ghost" onClick={() => setDismissed(gaveUp(asset))}>
            Dismiss
          </button>
        </p>
      )}

      <div className="asset-layout">
        <div className="asset-main">
          {mediaUrl && asset.kind === "video" && (
            <video
              ref={attach}
              className="viewer"
              src={mediaUrl}
              poster={poster}
              controls
              preload="metadata"
            />
          )}

          {manifest && manifest.thumbnails.length > 1 && (
            <Filmstrip
              spaceId={spaceId}
              assetId={asset.id}
              thumbnails={manifest.thumbnails}
              seek={seek}
            />
          )}
          {mediaUrl && asset.kind === "audio" && (
            <audio ref={attach} className="viewer" src={mediaUrl} controls />
          )}
          {mediaUrl && asset.kind === "image" && (
            <img className="viewer" src={mediaUrl} alt={asset.name} />
          )}

          {asset.summary && (
            <>
              <h2 className="section-head">Summary</h2>
              <p className="prose">{asset.summary}</p>
            </>
          )}

          {asset.topics.length > 0 && (
            <p className="corpus-topics">
              {asset.topics.map((topic) => (
                <span key={topic}>{topic}</span>
              ))}
            </p>
          )}

          {/* Everything that happens in this, on one ruler: what analysis
              found and what people concluded (ADR 000051). */}
          <AssetTimeline
            spaceId={spaceId as never}
            assetId={asset.id}
            manifest={manifest}
            moments={moments}
            at={at}
            seek={seek}
            video={element instanceof HTMLVideoElement ? element : null}
            mayEdit={space.data?.role !== "viewer"}
          />

          {manifest && (
            <Transcript
              manifest={manifest}
              at={at}
              seek={seek}
              following={following}
              onFollowingChange={setFollowing}
            />
          )}

          {!manifest ? (
            /* An asset nothing has analysed and one whose analysis this build
               cannot read are a shorter page for opposite reasons, and what
               somebody does next differs (ADR 000113). */
            <p
              className={manifestUnreadable ? "error" : "muted"}
              role={manifestUnreadable ? "alert" : undefined}
            >
              {manifestUnreadable
                ? "Its analysis holds something this version of scenix does not recognise, so the breakdown is not shown. Everything else about this is unaffected, and producing a stage again replaces what that stage wrote."
                : asset.status === "ready"
                  ? "No breakdown was produced for this."
                  : "The breakdown appears once analysis finishes."}
            </p>
          ) : (
            <Breakdown manifest={manifest} />
          )}

          {/* Only once there is analysis to add to. Without a manifest nothing
              is known about what is missing, which is not the same as nothing
              being missing, and the API refuses the request for that reason.
              A manifest this build could not read is not that case: the server
              read it and derived the stages below from it, and producing a
              stage again is how the asset is repaired (ADR 000113). */}
          {(manifest || manifestUnreadable) && (
            <Analysis
              spaceId={spaceId}
              assetId={asset.id}
              kind={asset.kind}
              engine={manifest?.analysisEngine}
              incomplete={incompleteStages}
              unfillable={unfillableStages}
              mayEdit={mayEdit}
              atRest={asset.status === "ready"}
            />
          )}
        </div>

        <aside className="asset-aside">
          <Related
            spaceId={spaceId}
            asset={asset}
            all={siblings.data ?? []}
            loading={siblings.isPending}
          />
        </aside>
      </div>

      <div className="danger-zone">
        <button
          type="button"
          className="ghost danger"
          disabled={remove.isPending}
          onClick={() => {
            if (
              window.confirm(
                `Remove "${asset.name}" from this space? You can restore it from Deleted later.`,
              )
            ) {
              remove.mutate();
            }
          }}
        >
          {remove.isPending ? "Removing…" : "Remove from this space"}
        </button>
        {remove.error && (
          <p role="alert" className="error">
            {describe(remove.error)}
          </p>
        )}
      </div>
    </section>
  );
}

/**
 * Names one give-up, so dismissing it dismisses that one and nothing else.
 *
 * The asset as well as the reason, because a space-wide pass gives up on many
 * assets with the same words, and the reason as well as the asset, because a
 * second pass on the same asset is a second thing to be told about.
 */
function gaveUp(asset: Pick<Asset, "id" | "failureReason">): string {
  return `${asset.id}\n${asset.failureReason}`;
}

/**
 * The stills the ingest worker pulled, spread across the video. Each one
 * jumps the player to the moment it was taken, so this doubles as a way to
 * skim a clip without watching it end to end (issue #3).
 */
function Filmstrip({
  spaceId,
  assetId,
  thumbnails,
  seek,
}: {
  spaceId: string;
  assetId: Asset["id"];
  thumbnails: AssetThumbnail[];
  seek: (seconds: number) => void;
}) {
  return (
    <ul className="filmstrip">
      {thumbnails.map((thumbnail, index) => (
        <li key={index}>
          <button type="button" onClick={() => seek(thumbnail.atSec)}>
            <img
              src={api.assetThumbnailAtUrl(spaceId as never, assetId, index, thumbnail.path)}
              loading="lazy"
              alt={`At ${timecode(thumbnail.atSec)}`}
            />
            <span className="at">{timecode(thumbnail.atSec)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * What analysis has not produced, and the way to ask for it (issue #313).
 *
 * The gap sentence is worth a line of its own because the alternative is a page
 * that is simply shorter: an asset analysed by somebody else's pipeline and one
 * whose vision pass failed look the same, and neither looks like a gap
 * (ADR 000073). Deliberately "not yet", not "failed". Most of the corpus has
 * never had these stages run at all, and calling that a failure would be wrong
 * about nearly every asset in it.
 *
 * The actions are here because for a long time the sentence was the whole of
 * it: a page that stated a gap, said the asset was fine, and offered nothing to
 * click. Filling what is missing is the safe default and needs no argument.
 * Overwriting has to name the stage and say whose work it would destroy, which
 * is the shape ADR 000063 gave the request and the shape the API's own refusal
 * uses.
 */
function Analysis({
  spaceId,
  assetId,
  kind,
  engine,
  incomplete,
  unfillable,
  mayEdit,
  atRest,
}: {
  spaceId: string;
  assetId: Asset["id"];
  kind: AssetKind;
  /** What produced the manifest, for saying what an overwrite would replace. */
  engine: string | undefined;
  incomplete: AnalysisStage[];
  /**
   * What no engine here could produce, whatever the manifest holds, and the
   * key each one is missing. Served rather than derived: which stages are paid
   * is fixed, and whether this deployment has paid for them is not
   * (ADR 000129).
   */
  unfillable: UnfillableStage[];
  mayEdit: boolean;
  /** Only an asset at rest can be re-analysed; one mid-pass already has a run. */
  atRest: boolean;
}) {
  const queryClient = useQueryClient();
  const [replacing, setReplacing] = useState<AnalysisStage[]>([]);

  const run = useMutation({
    mutationFn: (replace: AnalysisStage[]) =>
      api.reanalyzeAsset(spaceId as never, assetId, replace),
    onSuccess: async () => {
      setReplacing([]);
      await queryClient.invalidateQueries({ queryKey: ["asset", spaceId, assetId] });
    },
  });

  // Re-analysis is an editor write and costs money per pass, so a viewer reads
  // the gaps and is offered nothing: a control whose only outcome is a 403 is
  // worse than no control.
  const actionable = mayEdit && atRest;
  if (incomplete.length === 0 && !actionable) return null;

  const unrunnable = unfillable.map((gap) => gap.stage);

  // What a pass asked for now would actually produce. A gap whose engine has
  // no key here is filled by nothing, however many times it is asked for, and
  // the API refuses the request for that reason (ADR 000129).
  const fillable = incomplete.filter((stage) => !unrunnable.includes(stage));

  // Only what is there can be overwritten. A stage that has produced nothing is
  // what "analyse what is missing" is for, and listing it under a heading about
  // replacing would offer a destruction that is not on the table.
  //
  // And only what this deployment can run: a tick whose only outcome is a 409
  // is the control ADR 000080 left off, arriving by the other door. The
  // sentence below has already said why it is not on the list.
  const replaceable = stagesForKind(kind).filter(
    (stage) => !incomplete.includes(stage) && !unrunnable.includes(stage),
  );

  // What is both ticked and still on offer. A pass that landed while somebody
  // was deciding can empty a stage they had ticked, and a request has to be the
  // one the boxes on screen describe, not one carrying a tick nobody can see.
  const ticked = replacing.filter((stage) => replaceable.includes(stage));

  // Which button is waiting. react-query keeps the argument of the request in
  // flight, so telling them apart needs no second piece of state.
  const fillPending = run.isPending && run.variables?.length === 0;
  const replacePending = run.isPending && (run.variables?.length ?? 0) > 0;

  return (
    <section className="analysis">
      <p className="muted analysis-gaps">
        {incomplete.length > 0
          ? `Not analysed yet: ${incomplete.map((stage) => stageProduces(stage, kind)).join(", ")}.`
          : "Every stage that applies to this has produced something."}
      </p>

      {/* A fact about the deployment rather than about the asset, so it is
          drawn in the gap list's own register and not as an alarm: nothing is
          broken, a key is absent, and naming the key is the whole of what
          anybody can do about it (ADR 000129). */}
      {unfillable.length > 0 && (
        <p className="muted analysis-unfillable">
          {`This deployment has no ${listOf(
            unfillable.map((gap) => engineName(gap.engine)),
            "or",
          )} key, so ${listOf(
            unrunnable.map((stage) => stageProduces(stage, kind)),
          )} cannot be produced here.`}
        </p>
      )}

      {actionable && (
        <>
          <p className="analysis-fill">
            <button
              type="button"
              disabled={fillable.length === 0 || run.isPending}
              onClick={() => run.mutate([])}
            >
              {fillPending ? "Starting…" : "Analyse what is missing"}
            </button>
            {/* Said before the click rather than as a 409 after it: the API
                refuses a pass that would spend without changing anything, and
                finding that out by paying for the round trip is not a reason. */}
            {incomplete.length === 0 && (
              <span className="muted">Nothing is missing, so this would change nothing.</span>
            )}
            {/* Disabled and explained rather than gone. The gap is real and the
                thing missing is a key somebody can add, which is a different
                sentence from a stage that could never apply here. */}
            {incomplete.length > 0 && fillable.length === 0 && (
              <span className="muted">
                What is missing needs a key this deployment does not have, so this would change
                nothing.
              </span>
            )}
          </p>

          {replaceable.length > 0 && (
            <details className="analysis-replace">
              <summary>Produce something again</summary>
              <p className="muted">
                Overwrites what {engineLabel(engine)} produced. Anything left unticked stays exactly
                as it is.
              </p>
              <ul>
                {replaceable.map((stage) => (
                  <li key={stage}>
                    <label>
                      <input
                        type="checkbox"
                        checked={ticked.includes(stage)}
                        onChange={(event) =>
                          setReplacing((current) =>
                            event.target.checked
                              ? [...current, stage]
                              : current.filter((other) => other !== stage),
                          )
                        }
                      />
                      {stageProduces(stage, kind)}
                    </label>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                disabled={ticked.length === 0 || run.isPending}
                onClick={() => run.mutate(ticked)}
              >
                {replacePending ? "Starting…" : "Produce these again"}
              </button>
            </details>
          )}

          {run.error && (
            <p role="alert" className="error">
              {describe(run.error)}
            </p>
          )}
        </>
      )}
    </section>
  );
}

/**
 * What analysis found, over time. This is the part that lets somebody take the
 * high level and then dive in: the shape first, the detail underneath.
 */
function Breakdown({ manifest }: { manifest: AssetManifest }) {
  const words = manifest.transcript?.segments.length ?? 0;

  return (
    <>
      {manifest.visuals.length > 0 && (
        <>
          <h2 className="section-head">On screen</h2>
          <ul className="breakdown">
            {manifest.visuals.map((visual, i) => (
              <li key={`${visual.startSec}-${i}`}>
                <span className="at">{timecode(visual.startSec)}</span>
                <span>
                  {visual.description}
                  {visual.onScreenText && (
                    <span className="on-screen-text">{visual.onScreenText}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {manifest.audioEvents.length > 0 && (
        <>
          <h2 className="section-head">In the audio</h2>
          <ul className="breakdown">
            {manifest.audioEvents.map((event, i) => (
              <li key={`${event.startSec}-${i}`}>
                <span className="at">{timecode(event.startSec)}</span>
                <span>
                  <span className="event-kind">{event.kind}</span>
                  {event.description}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {words > 0 && (
        <>
          <h2 className="section-head">Transcript</h2>
          <ul className="breakdown">
            {manifest.transcript?.segments.map((segment, i) => (
              <li key={`${segment.startSec}-${i}`}>
                <span className="at">{timecode(segment.startSec)}</span>
                <span>
                  {segment.speaker && <span className="speaker">{segment.speaker}</span>}
                  {segment.text}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {manifest.shots.length > 0 && (
        <p className="muted">
          {manifest.shots.length === 1 ? "1 shot" : `${manifest.shots.length} shots`} detected.
        </p>
      )}
    </>
  );
}

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.userMessage;
  return error instanceof Error ? error.message : "Something went wrong.";
}

/**
 * Other content in this space worth looking at next.
 *
 * Ranked by shared topics, which is what the analysis already produces
 * (ADR 000024) and is a better signal than upload order. Falls back to the
 * most recent when nothing shares a topic, because an empty column is worse
 * than an imperfect one.
 */
function Related({
  spaceId,
  asset,
  all,
  loading,
}: {
  spaceId: string;
  asset: { id: string; topics: string[] };
  all: { id: string; name: string; kind: string; topics: string[]; durationSec: number }[];
  loading: boolean;
}) {
  const mine = new Set(asset.topics);

  const scored = all
    .filter((other) => other.id !== asset.id)
    .map((other) => ({
      other,
      shared: other.topics.filter((topic) => mine.has(topic)),
    }))
    .sort((a, b) => b.shared.length - a.shared.length)
    .slice(0, 6);

  if (loading) return <p className="muted">Loading…</p>;
  if (scored.length === 0) {
    return (
      <>
        <h2 className="section-head">Related</h2>
        <p className="muted">Nothing else in this space yet.</p>
      </>
    );
  }

  return (
    <>
      <h2 className="section-head">Related</h2>
      <ul className="related">
        {scored.map(({ other, shared }) => (
          <li key={other.id}>
            <Link to={`/spaces/${spaceId}/content/${other.id}`}>
              <strong>{other.name}</strong>
              <span className="related-meta">
                {other.kind}
                {other.durationSec > 0 && ` · ${timecode(other.durationSec)}`}
              </span>
              {shared.length > 0 && (
                <span className="related-shared">
                  {shared.slice(0, 3).map((topic) => (
                    <span key={topic}>{topic}</span>
                  ))}
                </span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}
